<template>
  <!-- MOSAIC LIBRARY -->
  <BaseModal
    ref="baseModalRef"
    :title="'Biblioteca de Mosaicos'"
    modal-id="mosaics"
    :is-visible="true"
    @close="closeDialog"
    @minimize="$emit('minimize', 'mosaics')"
    @restore="$emit('restore', 'mosaics')"
  >
    <div class="mosaic-container">
      <div class="container-fluid">
        <div class="form mosaic-window">
          <!-- Información de resultados -->
          <div class="col-12 info-header">
            <h5>Mostrando {{ mosaics.length }} mosaicos del último mes</h5>
          </div>

          <!-- INICIO TABLA -->
          <div class="row tbl-container">
            <div class="table-wrapper">
              <table class="table table-sm table-striped">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>UTC</th>
                   
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="mosaic in mosaics"
                    :key="mosaic.mosaic_id"
                    :class="{ 'selected-row': selectedMosaicId === mosaic.mosaic_id }"
                    @click="toggleRowSelection(mosaic)"
                  >
                    <td>{{ mosaic.mosaic_id }}</td>
                    <td>{{ mosaic.mosaic_name }}</td>
                    <td>{{ mosaic.description || 'Sin descripción' }}</td>
                    <td>{{ formatDate(mosaic.utc) }}</td>
                    
                  </tr>
                  <tr v-if="mosaics.length === 0">
                    <td colspan="5" class="text-center">
                      {{ loading ? 'Cargando mosaicos...' : 'No se encontraron mosaicos' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseModal>
  <!-- FIN MOSAIC LIBRARY -->
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import BaseModal from './base/BaseModal.vue'
import API from '@/services'
import { useGlobalStore } from '@/stores/global'
import type { MosaicLatest } from '../types'
import { storeToRefs } from 'pinia'
import { vi } from 'vuetify/locale'

const emit = defineEmits(['closed', 'mosaicSelected', 'minimize', 'restore'])
const global = useGlobalStore()
const {zoom, selectedMosaicId } = storeToRefs(global)

// Referencias
const baseModalRef = ref()
const mosaics = ref<MosaicLatest[]>([])
const loading = ref(false)

// Coordenadas aproximadas de Chile
const CHILE_BOUNDS = {
  min_lon: -75.7,  // Longitud oeste
  min_lat: -56.0,  // Latitud sur (incluyendo Antártica)
  max_lon: -66.4,  // Longitud este
  max_lat: -17.5   // Latitud norte
}

// Expone la función restoreModal para que App.vue pueda llamarla
defineExpose({
  restoreModal: () => {
    if (baseModalRef.value) {
      baseModalRef.value.restoreModal()
    }
  }
})

// Funciones
function closeDialog() {
  emit('closed')
}

function toggleRowSelection(mosaic: MosaicLatest) {
  if (selectedMosaicId.value === mosaic.mosaic_id) {
    selectedMosaicId.value = null
  } else {
    selectedMosaicId.value = mosaic.mosaic_id
    viewMosaic(mosaic)
  }
}

function viewMosaic(mosaic: MosaicLatest) {
  try {
    // Parsear el GeoJSON string para obtener las coordenadas
    const geoJson = JSON.parse(mosaic.mosaic_center_geojson)
    const coordinates = geoJson.coordinates // [lon, lat]
    global.zoom = 14 // Ajustar el zoom para ver el mosaico

    emit('mosaicSelected', coordinates)
  } catch (error) {
    console.error('Error parseando coordenadas del mosaico:', error)
  }
}

function formatDate(utc: string): string {
  if (!utc) return ''
  try {
    // El formato "2026-01-16T10:44:56.464-03:00" ya es ISO 8601 válido
    const date = new Date(utc)
    
    // Verificar que la fecha sea válida
    if (isNaN(date.getTime())) {
      return utc
    }
    
    return date.toLocaleString('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  } catch (error) {
    return utc
  }
}

async function loadMosaics() {
  loading.value = true
  try {
    // 720 horas = 30 días (aproximadamente un mes)
    const hours = 720
    const response = await API.listLatestMosaics(
      hours,
      CHILE_BOUNDS.min_lon,
      CHILE_BOUNDS.min_lat,
      CHILE_BOUNDS.max_lon,
      CHILE_BOUNDS.max_lat,
      100
    )
    
    // Limitar a 50 registros
    const allMosaics = response.data.Entries.Entry || []
    mosaics.value = allMosaics.slice(0, 50)
    
    console.log(`Cargados ${mosaics.value.length} mosaicos de los últimos 30 días`)
  } catch (error) {
    console.error('Error cargando mosaicos:', error)
    mosaics.value = []
  } finally {
    loading.value = false
  }
}

// Hooks de ciclo de vida
onMounted(() => {
  loadMosaics()
})
</script>

<style scoped>
.selected-row {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.selected-row:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

table > :not(caption) > * > * {
  background-color: #00000000;
  color: #ffffff;
  font-size: 12px;
  max-height: 100%;
}

.mosaic-container {
  width: 100%;
  height: 100%;
  z-index: 20;
}

.table {
  width: 100%;
  max-width: 100%;
  table-layout: auto;
}

.btn {
  margin: 0;
  padding: 2px 8px;
  font-size: 11px;
}

.btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.btn-primary:hover {
  background-color: #0b5ed7;
  border-color: #0a58ca;
}

.row {
  --bs-gutter-y: 0;
  display: flex;
  flex-wrap: wrap;
  flex-shrink: 0;
  max-width: 100%;
  margin: 10px;
  padding: 5px;
}

tbody {
  max-height: 100%;
  overflow-y: scroll;
}

.tbl-container {
  border: 0px solid white;
  border-radius: 5px;
  background-color: rgba(255, 255, 255, 0.1);
  padding: 5px;
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  height: calc(100% - 50px);
}

.table-wrapper {
  width: 100%;
  height: 100%;
  padding: 0;
  overflow-x: auto;
  overflow-y: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: rgb(74, 74, 74);
  color: white;
}

.container-fluid {
  padding: 0;
  margin: 0;
  max-height: 100%;
}

.form,
.container-fluid {
  height: 100%;
}

.mosaic-window {
  margin: 0px 10px;
  margin-top: 0px;
  --bs-gutter-y: 0;
  display: flex;
  flex-wrap: wrap;
  flex-shrink: 0;
  width: 100%;
  height: 100%;
}

.info-header {
  padding: 10px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  margin-bottom: 10px;
}

.info-header h5 {
  margin: 0;
  font-size: 14px;
  color: #ffffff;
}

.text-center {
  text-align: center;
  padding: 20px;
  color: rgba(255, 255, 255, 0.7);
}

.col-12 {
  height: max-content;
  padding: 0px 5px;
}

@media (max-width: 576px) {
  .modal-container {
    max-width: 90vw;
    min-width: 200px;
    width: 90vw;
    height: 480px;
    min-height: 400px;
  }
}
</style>
